import { useQuery } from '@tanstack/react-query';
import type { MorningHolidayCreateInput } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { type ChangeEvent, useRef, useState } from 'react';
import { useCreateHoliday, useDeleteHoliday, useUploadHolidayImage } from '../mutations';
import { morningHolidaysOptions } from '../queries';
import { CityScopeSelect } from './city-scope-select';

export function MorningHolidaysTab() {
  const { data: holidays = [] } = useQuery(morningHolidaysOptions());
  const create = useCreateHoliday();
  const del = useDeleteHoliday();
  const uploadImg = useUploadHolidayImage();
  const [scope, setScope] = useState('global');
  const [form, setForm] = useState({ date: '', label: '', greeting: '', cta: '' });
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const submit = () => {
    const body: MorningHolidayCreateInput = {
      date: form.date,
      // 'GE' is a hardcoded placeholder for country code — a proper country picker is a future improvement
      ...(scope === 'global' ? { countryCode: 'GE' } : { cityId: scope }),
      label: form.label || null,
      greeting: form.greeting || null,
      cta: form.cta || null,
    };
    create.mutate(body);
  };

  const onFile = (id: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadImg.mutate({ id, file });
  };

  return (
    <div className="space-y-6 p-4">
      <div className="space-y-2 rounded border p-3">
        <div className="text-sm font-medium">Новый праздник</div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">Привязка:</span>
          <CityScopeSelect value={scope} onChange={setScope} />
          <span className="text-xs text-muted-foreground">
            («Общие» = на страну по коду; уточняется отдельно)
          </span>
        </div>
        <div className="flex gap-2">
          <Input
            type="date"
            value={form.date}
            className="w-40"
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
          <Input
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Название (Новый год)"
          />
        </div>
        <Input
          value={form.greeting}
          onChange={(e) => setForm({ ...form, greeting: e.target.value })}
          placeholder="Праздничное приветствие (опц.)"
        />
        <Input
          value={form.cta}
          onChange={(e) => setForm({ ...form, cta: e.target.value })}
          placeholder="Праздничный призыв (опц.)"
        />
        <Button onClick={submit} disabled={!form.date}>
          Создать
        </Button>
      </div>

      <ul className="space-y-2">
        {holidays.map((h) => (
          <li key={h.id} className="flex items-center gap-3 rounded border p-2">
            {h.imageUrl ? <img src={h.imageUrl} alt="" className="h-12 w-12 rounded object-cover" /> : null}
            <span className="w-24 text-sm">{new Date(h.date).toLocaleDateString('ru-RU')}</span>
            <span className="flex-1">{h.label ?? h.greeting ?? '—'}</span>
            <span className="text-xs text-muted-foreground">
              {h.cityId ? 'город' : `страна ${h.countryCode ?? ''}`}
            </span>
            <input
              ref={(el) => {
                fileRefs.current[h.id] = el;
              }}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={onFile(h.id)}
            />
            <Button variant="ghost" size="sm" onClick={() => fileRefs.current[h.id]?.click()}>
              Картинка
            </Button>
            <Button variant="ghost" size="sm" onClick={() => del.mutate(h.id)}>
              Удалить
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
